/**
 * Grid system functions — pure (GridState, rng) => GridState transforms.
 *
 * Each factory returns a GridSystem. Systems clone only the layers they
 * write to; unmodified layers share references (structural sharing).
 *
 * Conservation-safe: flow and spread use delta accumulators.
 */

import {
  type GridState,
  getLayer,
  emptyLayer,
  forEachCell,
  cloneGridState,
  VON_NEUMANN,
  MOORE,
  sumNeighbours,
  countNeighboursAbove,
  getNeighbourValues,
} from './GridState';

export type GridTransform = (state: GridState, rng: () => number) => GridState;

// ── flow ──

/**
 * Move property down the gradient of another layer.
 * Water flowing downhill: flow('water', 'terrain', 0.25)
 */
export function flow(
  property: string,
  gradient: string,
  rate: number,
): GridTransform {
  return (state) => {
    const { cols, rows } = state;
    const prop = getLayer(state, property);
    const grad = getLayer(state, gradient);
    const offsets = VON_NEUMANN;

    const delta = emptyLayer(cols, rows);

    forEachCell(cols, rows, (c, r) => {
      const amount = prop[r][c];
      if (amount <= 0) return;

      // Height = gradient + property (pooling)
      const myHeight = grad[r][c] + amount;

      let totalDiff = 0;
      const lower: { c: number; r: number; diff: number }[] = [];

      for (const [dc, dr] of offsets) {
        const nc = c + dc;
        const nr = r + dr;
        if (nc < 0 || nc >= cols || nr < 0 || nr >= rows) continue;
        const nHeight = grad[nr][nc] + prop[nr][nc];
        if (nHeight < myHeight) {
          const diff = myHeight - nHeight;
          lower.push({ c: nc, r: nr, diff });
          totalDiff += diff;
        }
      }

      if (lower.length === 0 || totalDiff === 0) return;

      const moved = Math.min(amount, amount * rate);
      for (const { c: nc, r: nr, diff } of lower) {
        const share = (diff / totalDiff) * moved;
        delta[r][c] -= share;
        delta[nr][nc] += share;
      }
    });

    // Apply deltas
    const out = cloneGridState(state, [property]);
    const outProp = out.layers[property];
    forEachCell(cols, rows, (c, r) => {
      outProp[r][c] = Math.max(0, outProp[r][c] + delta[r][c]);
    });
    return out;
  };
}

// ── drift ──

/**
 * Shift a property in a fixed direction with toroidal wrapping.
 * Wind: drift('vapour', 1, 0, 0.3) — eastward
 */
export function drift(
  property: string,
  dx: number,
  dy: number,
  strength: number,
): GridTransform {
  return (state) => {
    const { cols, rows } = state;
    const prop = getLayer(state, property);
    const delta = emptyLayer(cols, rows);

    forEachCell(cols, rows, (c, r) => {
      const amount = prop[r][c];
      if (amount <= 0) return;

      const tc = ((c + dx) % cols + cols) % cols;
      const tr = ((r + dy) % rows + rows) % rows;
      const moved = amount * strength;
      delta[r][c] -= moved;
      delta[tr][tc] += moved;
    });

    const out = cloneGridState(state, [property]);
    const outProp = out.layers[property];
    forEachCell(cols, rows, (c, r) => {
      outProp[r][c] = Math.max(0, outProp[r][c] + delta[r][c]);
    });
    return out;
  };
}

// ── spread ──

/**
 * Diffuse a property evenly to neighbours.
 * Heat spreading: spread('heat', 0.1)
 */
export function spread(
  property: string,
  rate: number,
): GridTransform {
  const offsets = VON_NEUMANN;
  return (state) => {
    const { cols, rows } = state;
    const prop = getLayer(state, property);
    const delta = emptyLayer(cols, rows);

    forEachCell(cols, rows, (c, r) => {
      const amount = prop[r][c];
      if (amount <= 0) return;

      let nCount = 0;
      for (const [dc, dr] of offsets) {
        const nc = c + dc;
        const nr = r + dr;
        if (nc >= 0 && nc < cols && nr >= 0 && nr < rows) nCount++;
      }
      if (nCount === 0) return;

      const shared = amount * rate / nCount;
      for (const [dc, dr] of offsets) {
        const nc = c + dc;
        const nr = r + dr;
        if (nc < 0 || nc >= cols || nr < 0 || nr >= rows) continue;
        delta[r][c] -= shared;
        delta[nr][nc] += shared;
      }
    });

    const out = cloneGridState(state, [property]);
    const outProp = out.layers[property];
    forEachCell(cols, rows, (c, r) => {
      outProp[r][c] = Math.max(0, outProp[r][c] + delta[r][c]);
    });
    return out;
  };
}

// ── convert ──

/**
 * Transfer between layers, optionally influenced by a third.
 * Evaporation: convert('water', 'vapour', 0.05, { layer: 'terrain', inverse: true })
 */
export function convert(
  from: string,
  to: string,
  rate: number,
  influence?: { layer: string; inverse?: boolean },
): GridTransform {
  return (state) => {
    const { cols, rows } = state;
    const fromLayer = getLayer(state, from);
    const toLayer = getLayer(state, to);
    const infLayer = influence ? getLayer(state, influence.layer) : null;

    const out = cloneGridState(state, [from, to]);
    const outFrom = out.layers[from];
    const outTo = out.layers[to];

    forEachCell(cols, rows, (c, r) => {
      const amount = fromLayer[r][c];
      if (amount <= 0) return;

      let factor = 1;
      if (infLayer) {
        const v = Math.max(0, Math.min(1, infLayer[r][c]));
        factor = influence!.inverse ? 1 - v : v;
      }

      const converted = Math.min(amount, amount * rate * factor);
      outFrom[r][c] = fromLayer[r][c] - converted;
      outTo[r][c] = toLayer[r][c] + converted;
    });

    return out;
  };
}

// ── grow ──

/**
 * Logistic growth of a property.
 * Prey reproduction: grow('prey', 0.1, 1.0)
 */
export function grow(
  property: string,
  rate: number,
  capacity: number,
  influence?: { layer: string; inverse?: boolean },
): GridTransform {
  return (state) => {
    const { cols, rows } = state;
    const prop = getLayer(state, property);
    const infLayer = influence ? getLayer(state, influence.layer) : null;

    const out = cloneGridState(state, [property]);
    const outProp = out.layers[property];

    forEachCell(cols, rows, (c, r) => {
      const amount = prop[r][c];
      if (amount <= 0) return;

      let factor = 1;
      if (infLayer) {
        const v = Math.max(0, Math.min(1, infLayer[r][c]));
        factor = influence!.inverse ? 1 - v : v;
      }

      const growth = amount * rate * (1 - amount / capacity) * factor;
      outProp[r][c] = Math.min(capacity, Math.max(0, amount + growth));
    });

    return out;
  };
}

// ── decay ──

/**
 * Exponential decay of a property.
 * Pheromone evaporate: decay('scent', 0.1)
 */
export function decay(
  property: string,
  rate: number,
  influence?: { layer: string; inverse?: boolean },
): GridTransform {
  return (state) => {
    const { cols, rows } = state;
    const prop = getLayer(state, property);
    const infLayer = influence ? getLayer(state, influence.layer) : null;

    const out = cloneGridState(state, [property]);
    const outProp = out.layers[property];

    forEachCell(cols, rows, (c, r) => {
      const amount = prop[r][c];
      if (amount <= 0) return;

      let factor = 1;
      if (infLayer) {
        const v = Math.max(0, Math.min(1, infLayer[r][c]));
        factor = influence!.inverse ? 1 - v : v;
      }

      const loss = amount * rate * factor;
      outProp[r][c] = Math.max(0, amount - loss);
    });

    return out;
  };
}

// ── neighbourReduce ──

export type ReduceMode = 'sum' | 'count' | 'avg';
export type OffsetPreset = 'moore' | 'vonneumann';

const OFFSET_MAP: Record<OffsetPreset, ReadonlyArray<readonly [number, number]>> = {
  moore: MOORE,
  vonneumann: VON_NEUMANN,
};

/**
 * Compute a neighbour aggregate and write to a target layer.
 * - sum: sum of neighbour values
 * - count: count of neighbours above threshold (0.5)
 * - avg: average of neighbour values
 *
 * GoL step 1: neighbourReduce('alive', 'ncount', 'count', 'moore')
 */
export function neighbourReduce(
  source: string,
  target: string,
  mode: ReduceMode,
  offsets: OffsetPreset,
  wrap = true,
): GridTransform {
  return (state) => {
    const { cols, rows } = state;
    const src = getLayer(state, source);
    const offs = OFFSET_MAP[offsets] ?? MOORE;
    const result = emptyLayer(cols, rows);

    forEachCell(cols, rows, (c, r) => {
      if (mode === 'count') {
        result[r][c] = countNeighboursAbove(src, cols, rows, c, r, offs, wrap, 0.5);
      } else if (mode === 'sum') {
        result[r][c] = sumNeighbours(src, cols, rows, c, r, offs, wrap);
      } else {
        const vals = getNeighbourValues(src, cols, rows, c, r, offs, wrap);
        result[r][c] = vals.length > 0
          ? vals.reduce((a, b) => a + b, 0) / vals.length
          : 0;
      }
    });

    const out = cloneGridState(state, []);
    out.layers[target] = result;
    return out;
  };
}

// ── threshold ──

/**
 * Apply conditional step function for cellular automata.
 * Reads a source layer and a count layer, writes to a target layer.
 *
 * Rules:
 * - If source > 0.5 (alive) and surviveMin <= count <= surviveMax → 1
 * - If source <= 0.5 (dead) and birthMin <= count <= birthMax → 1
 * - Otherwise → 0
 *
 * GoL step 2: threshold('alive', 'ncount', 'alive', 3, 3, 2, 3)
 */
export function threshold(
  source: string,
  count: string,
  target: string,
  birthMin: number,
  birthMax: number,
  surviveMin: number,
  surviveMax: number,
): GridTransform {
  return (state) => {
    const { cols, rows } = state;
    const src = getLayer(state, source);
    const cnt = getLayer(state, count);
    const result = emptyLayer(cols, rows);

    forEachCell(cols, rows, (c, r) => {
      const isAlive = src[r][c] > 0.5;
      const n = cnt[r][c];
      if (isAlive) {
        result[r][c] = (n >= surviveMin && n <= surviveMax) ? 1 : 0;
      } else {
        result[r][c] = (n >= birthMin && n <= birthMax) ? 1 : 0;
      }
    });

    const out = cloneGridState(state, []);
    out.layers[target] = result;
    return out;
  };
}

